import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import useResponsive from "../../hooks/useResponsive";
import WorkExperienceStep from "../../components/projects/projectApplication/WorkExperienceStep";
import ProjectApplicationMobileStepper from "../../components/projects/projectApplication/ProjectApplicationMobileStepper";
import ProjectApplicationStepper from "../../components/projects/projectApplication/ProjectApplicationStepper";
import TheoryStep from "../../components/projects/projectApplication/TheoryStep";
import CommitmentStep from "../../components/projects/projectApplication/CommitmentStep";
import AvailabilityStep from "../../components/projects/projectApplication/AvailabilityStep";
import MotivationStep from "../../components/projects/projectApplication/MotivationStep";
import { ProjectApplicationDto } from "../../types/projectTypes";
import { CheckCircle } from "@mui/icons-material";
import { Link, useNavigate, useParams } from "react-router-dom";
import useProjects from "../../hooks/projects/useProjects";
import useProjectsDetails from "../../hooks/projects/useProjectsDetails";
import dayjs from "dayjs";
import LoadingCircle from "../../components/general/LoadingCircle";
import { hasDayPassed } from "../../utils/dateUtils";

// Steps for the project application form
const steps = [
  {
    label: "Berufserfahrung",
    description: "Relevante praktische Berufserfahrung",
  },
  {
    label: "Theoriekenntnisse",
    description: "Relevante Theoriekenntnisse",
  },
  {
    label: "Vereinsengagement",
    description: "Vereinsengagement",
  },
  {
    label: "Verfügbarkeit",
    description: "Verfügbarkeit",
  },
  {
    label: "Motivation",
    description: "Motivation für das Projekt",
  },
];

/**
 * Project application form
 * @returns the respective stepper for the project application form
 */
const ProjectApplication = () => {
  const isMobile = useResponsive("down", "sm");
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [isApplicationCompleted, setIsApplicationCompleted] = React.useState(false);
  const maxSteps = steps.length;
  const { applicationSuggestions, saveApplication } = useProjects();
  const { projectDetails, userApplication, isUserApplicationLoading } = useProjectsDetails(Number(id));
  const [applicationData, setApplicationData] = React.useState<ProjectApplicationDto>({
    projectId: Number(id),
    internship: userApplication?.internship ?? "",
    apprenticeship: "",
    studentJob: userApplication?.studentJob ?? "",
    seminarPapers: userApplication?.seminarPapers ?? "",
    workshops: userApplication?.workshops ?? [],
    additionalWorkshops: userApplication?.additionalWorkshops ?? "",
    internalCommitment: userApplication?.internalCommitment ?? [],
    preliminaryWork: userApplication?.preliminaryWork ?? [],
    extraordinaryCommitment: userApplication?.extraordinaryCommitment ?? null,
    availability: userApplication?.availability ?? null,
    restriction: userApplication?.restriction ?? "",
    motivation: userApplication?.motivation ?? "",
  });

  useEffect(() => {
    const hasDeadline1Ended =
      !projectDetails?.applicationEnd2 &&
      projectDetails?.applicationEnd1 &&
      hasDayPassed(projectDetails.applicationEnd1);
    const hasDeadline2Ended = hasDayPassed(projectDetails?.applicationEnd2);

    if ((projectDetails && projectDetails.status !== "Bewerbung") || hasDeadline1Ended || hasDeadline2Ended) {
      navigate("/projekte");
    }
  }, [projectDetails]);

  useEffect(() => {
    if (userApplication) {
      setApplicationData(userApplication);
    }
  }, [userApplication]);

  const applicationDeadline = projectDetails?.applicationEnd2
    ? dayjs(projectDetails?.applicationEnd2).format("DD.MM.YYYY HH:mm")
    : projectDetails?.applicationEnd1
    ? dayjs(projectDetails?.applicationEnd1).format("DD.MM.YYYY HH:mm")
    : "";

  const [secrecyAgreement, setSecrecyAgreement] = React.useState(false);

  // Get the total number of steps
  const totalSteps = () => {
    return steps.length;
  };

  // Check if the current step is the last step
  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };
  // Handles the next step
  const handleNext = () => {
    isLastStep() ? setActiveStep(0) : setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Handles the previous step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle click on step
  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  // Handles the completion of the application
  const handleCompleteApplication = async () => {
    try {
      await saveApplication(applicationData);
      setIsApplicationCompleted(true);
    } catch (error) {
      // do nothing since the error is handled in the saveApplication mutation
    }
  };

  // Check if all required fields are filled
  const checkRequiredFields = () => {
    if (applicationData.availability === "Mit Einschränkung" && applicationData.restriction === "") {
      return true;
    }
    if (secrecyAgreement && applicationData.availability !== null) {
      return false;
    }
    return true;
  };

  // Renders the respective step
  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <WorkExperienceStep
            applicationData={applicationData}
            setApplicationData={setApplicationData}
            itSkillsSuggestions={applicationSuggestions.ItSkills}
            internshipsSuggestions={applicationSuggestions.internships}
          />
        );
      case 1:
        return (
          <TheoryStep
            applicationData={applicationData}
            setApplicationData={setApplicationData}
            workshops={applicationSuggestions.visitedWorkshops}
          />
        );
      case 2:
        return (
          <CommitmentStep
            applicationData={applicationData}
            setApplicationData={setApplicationData}
            internalCommitmentSuggestions={applicationSuggestions.positionsOfMember}
            workshopsHeldByMemberSuggestions={applicationSuggestions.workshopsHeldByMember}
          />
        );
      case 3:
        return <AvailabilityStep applicationData={applicationData} setApplicationData={setApplicationData} />;
      case 4:
        return (
          <MotivationStep
            applicationData={applicationData}
            setApplicationData={setApplicationData}
            secrecyAgreement={secrecyAgreement}
            setSecrecyAgreement={setSecrecyAgreement}
          />
        );
      default:
        return <div>Step {step}</div>;
    }
  };

  if (isUserApplicationLoading) {
    return <LoadingCircle />;
  }

  return (
    <Box sx={{ flexGrow: 1, height: "100%", flex: 1 }}>
      <Stack direction={"column"} sx={{ marginBottom: 1 }}>
        <Typography component="h1" fontSize={24} fontWeight={"bold"} sx={{ marginBottom: 0 }}>
          Bewerbung auf externes Projekt{" "}
        </Typography>
        <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"space-between"}>
          <Typography variant="body1" component="span" fontStyle={"italic"}>
            {projectDetails?.projectName}
          </Typography>
          <Typography variant="body1" component="span">
            Bewebungsfrist: {applicationDeadline}
          </Typography>
        </Stack>
      </Stack>
      {!isApplicationCompleted ? (
        isMobile ? (
          <ProjectApplicationMobileStepper
            activeStep={activeStep}
            handleBack={handleBack}
            handleNext={handleNext}
            maxSteps={maxSteps}
            renderStep={renderStep}
            steps={steps}
            isLastStep={isLastStep}
            handleComplete={handleCompleteApplication}
            checkRequiredFields={checkRequiredFields}
          />
        ) : (
          <ProjectApplicationStepper
            activeStep={activeStep}
            handleBack={handleBack}
            handleNext={handleNext}
            handleStep={handleStep}
            renderStep={renderStep}
            steps={steps}
            isLastStep={isLastStep}
            handleComplete={handleCompleteApplication}
            checkRequiredFields={checkRequiredFields}
          />
        )
      ) : (
        <Paper
          sx={{
            margin: isMobile ? 2 : 0,
            minHeight: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Stack direction={"column"} alignItems={"center"} spacing={5}>
            <Stack direction={"column"} alignItems={"center"} spacing={1}>
              <CheckCircle color="success" sx={{ fontSize: 50 }} />
              <Typography fontWeight={"bold"} gutterBottom align="center">
                Deine Bewerbung wurde erfolgreich abgeschickt!
              </Typography>
            </Stack>
            <Link to={"/projekte"} style={{ textDecoration: "none" }}>
              <Button color={"primary"} variant="contained">
                Zurück zu den Projekten
              </Button>
            </Link>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default ProjectApplication;
